/**
 * 内联预览组件
 * 点击引用标记后展开显示文档片段预览
 */

import React from 'react';
import { FileText, ChevronDown, ExternalLink, BookOpen } from 'lucide-react';

export interface InlinePreviewProps {
    isExpanded: boolean;
    documentName: string;
    documentId: string;
    pageNumber?: number;
    totalPages?: number;
    previewText: string;
    chunkIndex?: number;
    onViewFullDocument: () => void;
    onClose: () => void;
}

export const InlinePreview: React.FC<InlinePreviewProps> = ({
    isExpanded,
    documentName,
    documentId,
    pageNumber,
    totalPages,
    previewText,
    chunkIndex,
    onViewFullDocument,
    onClose,
}) => {
    if (!isExpanded) return null;

    // 截断预览文本
    const truncatedPreview = previewText.length > 300
        ? previewText.slice(0, 300) + '...'
        : previewText;

    return (
        <div className="mt-2 mb-3 animate-in slide-in-from-top-2 duration-200">
            <div className="relative bg-gradient-to-br from-white to-purple-50/30 dark:from-gray-800 dark:to-purple-900/10
                      border border-purple-200/60 dark:border-purple-700/40
                      rounded-xl shadow-lg shadow-purple-500/10
                      overflow-hidden">
                {/* 顶部装饰条 */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-400" />

                {/* 头部 */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-purple-100 dark:border-purple-800/30">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-gradient-to-br from-purple-100 to-cyan-100 dark:from-purple-900/50 dark:to-cyan-900/50 rounded-lg">
                            <FileText className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                        </div>
                        <div>
                            <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 truncate max-w-[200px]">
                                {documentName}
                            </h4>
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                                {pageNumber && (
                                    <span className="px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded">
                                        第 {pageNumber} 页{totalPages ? ` / ${totalPages}` : ''}
                                    </span>
                                )}
                                {chunkIndex !== undefined && (
                                    <span className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">
                                        段落 {chunkIndex + 1}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    <button
                        onClick={onClose}
                        className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                        title="收起预览"
                    >
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                    </button>
                </div>

                {/* 预览内容 */}
                <div className="px-4 py-3">
                    <div className="relative pl-3 border-l-2 border-purple-300 dark:border-purple-600">
                        <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
                            {truncatedPreview}
                        </p>
                    </div>
                </div>

                {/* 底部操作 */}
                <div className="flex items-center justify-between px-4 py-2 bg-gray-50/50 dark:bg-gray-900/30 border-t border-gray-100 dark:border-gray-700/30">
                    <span className="text-xs text-gray-400">
                        文档 ID: {documentId.slice(0, 8)}...
                    </span>

                    <button
                        onClick={onViewFullDocument}
                        className="flex items-center gap-1.5 px-3 py-1.5 
                       bg-gradient-to-r from-purple-500 to-cyan-500 
                       hover:from-purple-600 hover:to-cyan-600
                       text-white text-xs font-medium rounded-lg
                       transition-all duration-200 hover:shadow-md hover:shadow-purple-500/25"
                    >
                        <BookOpen className="w-3.5 h-3.5" />
                        <span>查看完整文档</span>
                        <ExternalLink className="w-3 h-3" />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default InlinePreview;
